import * as React from 'react';
import {
  type ContextualExpression,
  stripAll,
  getAnn,
} from '@packages/utilities/contextual-expression';
import {getMeta, Types, TypeImplementation} from '@packages/systems/wfdl';
import {useTheme} from '@packages/utilities/styled';
import TextInput from '@packages/systems/design-system/TextInput';
import TextArea from '@packages/systems/design-system/Textarea';
import Hitarea from '@packages/systems/design-system/Hitarea';
import {getListOfElementsFromString} from '@packages/systems/ExpressionEditor-utils-universal';
import {
  updateExpressionAction,
  useExpressionEditorImplementationContext,
} from '@packages/systems/ExpressionEditor-utils';
import {Checkbox} from '@packages/systems/design-system/Checkbox';
import {Label} from '@packages/systems/ExpressionEditor-utils-ui';
import {getElementContentFromListOfElements} from '@packages/utilities/element-content';

type ChildrenPrimitiveTypeMeta = {
  multiLine?: boolean;
  placeholder?: string;
};

const defaultTextMeta: ChildrenPrimitiveTypeMeta = {};

// formerly ChildrenPrimitiveType
export const __DEPRECATED__ChildrenPrimitiveType = TypeImplementation.Custom({
  render: function ChildrenPrimitive({expr, dispatch}) {
    const theme = useTheme();
    const {env} = useExpressionEditorImplementationContext();
    const type = getAnn(expr)?.type;

    const listType = type ? Types.getListItemType(type) : null;
    // NOTE: We use `listType.meta` here since we're working directly with a
    // ListDataType:
    const maybeMeta = listType ? getMeta(listType) : null;
    const meta = maybeMeta ? maybeMeta : defaultTextMeta;

    const [{text, existingId}, setValue] = React.useState(
      getElementContentFromListOfElements(stripAll(expr))
    );

    React.useLayoutEffect(() => {
      setValue(getElementContentFromListOfElements(stripAll(expr)));
    }, [expr]);

    const onFocus = React.useCallback(() => {
      env.transaction.begin();
    }, [env.transaction]);

    const onBlur = React.useCallback(() => {
      env.transaction.commit();
    }, [env.transaction]);

    const onChange = React.useCallback(
      (e: React.ChangeEvent<HTMLInputElement>) => {
        const elementContent = {text: e.target.value, existingId};
        setValue(elementContent);
        dispatch(
          updateExpressionAction(getListOfElementsFromString(elementContent))
        );
      },
      [dispatch, existingId]
    );

    if (meta.multiLine) {
      const placeholderStyles = {
        padding: theme.gutters.base,
      };
      const style = {
        minHeight: theme.gutters.base * 9,
        padding: theme.gutters.base,
        zIndex: undefined,
      };
      return (
        <TextArea
          placeholderStyles={placeholderStyles}
          data-automation-id={`${
            type ? Types.generateAutomationIds(type) : 'children-primitive'
          }`}
          style={style}
          onFocus={onFocus}
          onBlur={onBlur}
          onChange={onChange}
          placeholderValue={meta.placeholder}
          text={text}
          rows={5}
          selectAll
        />
      );
    }

    return (
      <Hitarea>
        <TextInput
          type="text"
          data-automation-id={`${
            type ? Types.generateAutomationIds(type) : 'children-primitive'
          }`}
          placeholder={meta.placeholder}
          value={text}
          onFocus={onFocus}
          onBlur={onBlur}
          onChange={onChange}
        />
      </Hitarea>
    );
  },
  renderSummary: ({expr: _expr}: {expr: ContextualExpression}) => {
    // We have nothing that would show single elements in a list currently, once
    // we do we can improve this logic.
    return <span>Element</span>;
  },
  system: {
    defaultValue: [],
    displayName: 'Text',
    label: 'Text',
  },
  metaUI: ({meta, onChange}) => {
    const state = Boolean(meta?.multiLine) || false;
    return (
      <>
        <Label>Multiline</Label>
        <Checkbox
          aria-label="Multiline"
          checked={state}
          onChange={() => {
            const newMeta = {...meta, multiLine: !state};
            onChange(newMeta);
          }}
        />
      </>
    );
  },
});
